import React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X, Download, Eye, FileText, Image, Video, Music, Archive } from 'lucide-react'
import { cn } from '../../../utils/styles'
import { formatFileSize } from '../utils/fileUtils'
import type { FileWithUIState } from '../types'

export interface FilePreviewDialogProps {
  file: FileWithUIState | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onDownload?: (file: FileWithUIState) => void
}

export function FilePreviewDialog({
  file,
  open,
  onOpenChange,
  onDownload
}: FilePreviewDialogProps) {
  if (!file) return null

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="h-8 w-8 text-blue-500" />
    if (mimeType.startsWith('video/')) return <Video className="h-8 w-8 text-purple-500" />
    if (mimeType.startsWith('audio/')) return <Music className="h-8 w-8 text-green-500" />
    if (mimeType.includes('pdf')) return <FileText className="h-8 w-8 text-red-500" />
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) {
      return <Archive className="h-8 w-8 text-yellow-500" />
    }
    return <FileText className="h-8 w-8 text-gray-500" />
  }

  const canPreview = (mimeType: string): boolean => {
    return (
      mimeType.startsWith('image/') ||
      mimeType.startsWith('video/') ||
      mimeType.startsWith('audio/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/')
    )
  }

  const renderPreview = () => {
    const mimeType = file.file.type
    const previewUrl = file.previewUrl

    if (mimeType.startsWith('image/') && previewUrl) {
      return (
        <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4 min-h-[400px]">
          <img
            src={previewUrl}
            alt={file.file.name}
            className="max-w-full max-h-[400px] object-contain rounded"
          />
        </div>
      )
    }

    if (mimeType.startsWith('video/') && previewUrl) {
      return (
        <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4">
          <video
            controls
            className="max-w-full max-h-[400px] rounded"
            preload="metadata"
          >
            <source src={previewUrl} type={mimeType} />
            Your browser does not support video playback.
          </video>
        </div>
      )
    }

    if (mimeType.startsWith('audio/') && previewUrl) {
      return (
        <div className="flex items-center justify-center bg-gray-50 rounded-lg p-8">
          <div className="text-center space-y-4">
            <Music className="h-16 w-16 text-green-500 mx-auto" />
            <audio controls className="w-full max-w-md">
              <source src={previewUrl} type={mimeType} />
              Your browser does not support audio playback.
            </audio>
          </div>
        </div>
      )
    }

    if (mimeType === 'application/pdf' && previewUrl) {
      return (
        <div className="bg-gray-50 rounded-lg p-4">
          <embed
            src={previewUrl}
            type="application/pdf"
            width="100%"
            height="400px"
            className="rounded"
          />
        </div>
      )
    }

    // Fallback for non-previewable files
    return (
      <div className="flex items-center justify-center bg-gray-50 rounded-lg p-12">
        <div className="text-center space-y-4">
          {getFileIcon(mimeType)}
          <div>
            <p className="text-sm font-medium text-gray-900">Preview not available</p>
            <p className="text-xs text-gray-500">
              This file type cannot be previewed in the browser
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />

        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {getFileIcon(file.file.type)}
              <div>
                <DialogPrimitive.Title className="text-lg font-semibold text-gray-900">
                  {file.file.name}
                </DialogPrimitive.Title>
                <p className="text-sm text-gray-500">
                  {formatFileSize(file.file.size)} • {file.file.type || 'Unknown type'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {onDownload && (
                <button
                  onClick={() => onDownload(file)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  title="Download file"
                >
                  <Download className="h-5 w-5" />
                </button>
              )}

              <DialogPrimitive.Close className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {renderPreview()}
          </div>

          {/* Footer with file metadata */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 rounded-b-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Size:</span>
                <span className="ml-2 text-gray-600">{formatFileSize(file.file.size)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Type:</span>
                <span className="ml-2 text-gray-600">{file.file.type || 'Unknown'}</span>
              </div>
              {file.file.lastModified && (
                <div>
                  <span className="font-medium text-gray-700">Modified:</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(file.file.lastModified).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className={cn(
                  'ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                  {
                    'bg-green-100 text-green-800': file.status === 'uploaded',
                    'bg-blue-100 text-blue-800': file.status === 'uploading',
                    'bg-red-100 text-red-800': file.status === 'error',
                    'bg-gray-100 text-gray-800': file.status === 'pending'
                  }
                )}>
                  {file.status}
                </span>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}